import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';

// "Delete my account" for every FHO app menu. The confirmation window is mounted on its
// own, outside the menu, so it stays open when the menu closes behind it.
const RED = '#B42318';

const DeleteAccountModal = ({ onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const ready = password.length > 0 && confirm.trim().toUpperCase() === 'DELETE' && !busy;

  const submit = () => {
    if (!ready) return;
    setBusy(true); setError('');
    let token = null;
    try { token = localStorage.getItem('token'); } catch (e) { /* storage blocked */ }
    fetch('/api/v1/account/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ password, confirm: confirm.trim().toUpperCase() }),
    }).then((r) => r.json().catch(() => ({})).then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { setError((d && d.detail) || 'Your account could not be deleted. Please try again.'); setBusy(false); return; }
        try { localStorage.clear(); } catch (e) { /* storage blocked */ }
        setDone(true); setBusy(false);
      })
      .catch(() => { setError('Your account could not be deleted. Please check your connection and try again.'); setBusy(false); });
  };

  const input = { width: '100%', boxSizing: 'border-box', minHeight: '48px', padding: '12px 14px', fontSize: '16px', borderRadius: '10px', border: '1.5px solid #CBD2DC', color: '#1A1D2E', background: '#FFFFFF', fontFamily: 'inherit' };
  const label = { display: 'block', fontSize: '15px', fontWeight: 600, color: '#1A1D2E', margin: '16px 0 6px' };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="fho-del-title"
      onClick={() => { if (!busy && !done) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,15,30,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5000, padding: '16px' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#FFFFFF', color: '#1A1D2E', borderRadius: '16px', width: '100%', maxWidth: '460px', maxHeight: '90vh', overflowY: 'auto', padding: '24px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', fontFamily: 'inherit', textAlign: 'left' }}>
        {done ? (
          <>
            <h2 id="fho-del-title" style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Your account has been deleted</h2>
            <p style={{ margin: '10px 0 20px', fontSize: '16px', lineHeight: 1.5, color: '#3D4656' }}>
              Your conversations, profile and progress have been removed. Thank you for spending time with us.
            </p>
            <button type="button" onClick={() => { window.location.href = '/'; }}
              style={{ width: '100%', minHeight: '48px', borderRadius: '12px', border: 'none', background: '#1A1D2E', color: '#FFFFFF', fontSize: '16px', fontWeight: 700, cursor: 'pointer' }}>Close</button>
          </>
        ) : (
          <>
            <h2 id="fho-del-title" style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>Delete your account?</h2>
            <p style={{ margin: '10px 0 0', fontSize: '16px', lineHeight: 1.5, color: '#3D4656' }}>
              This permanently deletes your account and everything saved under your email: your conversations, your profile and setup answers, your check-ins and your progress. It cannot be undone.
            </p>
            <label htmlFor="fho-del-pw" style={label}>Your password</label>
            <input id="fho-del-pw" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} style={input} />
            <label htmlFor="fho-del-confirm" style={label}>Type DELETE to confirm</label>
            <input id="fho-del-confirm" type="text" autoComplete="off" autoCapitalize="characters" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} style={input} />
            {error && <p role="alert" style={{ margin: '14px 0 0', fontSize: '15px', lineHeight: 1.45, color: RED }}>{error}</p>}
            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button type="button" onClick={onClose} disabled={busy}
                style={{ flex: 1, minHeight: '48px', borderRadius: '12px', border: '1.5px solid #CBD2DC', background: '#FFFFFF', color: '#1A1D2E', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button type="button" onClick={submit} disabled={!ready}
                style={{ flex: 1.4, minHeight: '48px', borderRadius: '12px', border: 'none', background: ready ? RED : '#E7B8B3', color: '#FFFFFF', fontSize: '16px', fontWeight: 700, cursor: ready ? 'pointer' : 'not-allowed' }}>
                {busy ? 'Deleting...' : 'Delete my account'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const openDeleteAccount = () => {
  if (typeof document === 'undefined') return;
  const node = document.createElement('div');
  document.body.appendChild(node);
  const root = createRoot(node);
  const close = () => {
    setTimeout(() => {
      try { root.unmount(); } catch (e) { /* already gone */ }
      if (node.parentNode) node.parentNode.removeChild(node);
    }, 0);
  };
  root.render(<DeleteAccountModal onClose={close} />);
};

// variant "dark" for dark menus (SAFE, Mental Performance), "light" for white menus.
const DeleteAccountButton = ({ variant = 'light', onOpen }) => {
  const dark = variant === 'dark';
  return (
    <button type="button" onClick={() => { if (onOpen) onOpen(); openDeleteAccount(); }}
      style={{ width: '100%', boxSizing: 'border-box', minHeight: '40px', marginTop: '6px', padding: '8px 12px', background: 'transparent', border: `1px solid ${dark ? 'rgba(255,180,171,0.45)' : '#F2C4C0'}`, borderRadius: '10px', color: dark ? '#FFB4AB' : RED, cursor: 'pointer', fontSize: '14px', fontFamily: 'inherit', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}>
      Delete my account
    </button>
  );
};

export default DeleteAccountButton;
